import React, { useEffect, useRef } from "react";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import styled from "@emotion/styled/macro";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import nearbyCustomerMapPin from "../../assets/nearby-customer-map-pin.svg";
import prospectMapPin from "../../assets/prospect-map-pin.svg";
import {
  getClosestLocations,
  NearbyCustomerLocation,
} from "../../../services/nearbyCustomers";

const GOOGLE_MAPS_API_KEYS = {
  staging: "AIzaSyBUajv7H7lc6-yNr2ZZAaq_uXmbUH16x7I",
  production: "AIzaSyAJbuffPDEkA6ra_88XShQjN-0acodFvUY",
} as const;

const DEFAULT_MAP_ZOOM = 14;
const MIN_NEIGHBORS_TO_SHOW = 20;
const MIN_MAP_SPAN_DELTA = 0.002;

const centerMapOnProspectWithNeighbors = (
  map: google.maps.Map,
  prospectPosition: google.maps.LatLngLiteral,
  locations: NearbyCustomerLocation[]
) => {
  const closestNeighbors = getClosestLocations(
    locations,
    prospectPosition.lat,
    prospectPosition.lng,
    MIN_NEIGHBORS_TO_SHOW
  );

  if (closestNeighbors.length === 0) {
    map.setCenter(prospectPosition);
    map.setZoom(DEFAULT_MAP_ZOOM);
    return;
  }

  let maxLatDelta = MIN_MAP_SPAN_DELTA;
  let maxLngDelta = MIN_MAP_SPAN_DELTA;

  closestNeighbors.forEach((location) => {
    maxLatDelta = Math.max(
      maxLatDelta,
      Math.abs(location.latitude - prospectPosition.lat)
    );
    maxLngDelta = Math.max(
      maxLngDelta,
      Math.abs(location.longitude - prospectPosition.lng)
    );
  });

  const bounds = new google.maps.LatLngBounds(
    {
      lat: prospectPosition.lat - maxLatDelta,
      lng: prospectPosition.lng - maxLngDelta,
    },
    {
      lat: prospectPosition.lat + maxLatDelta,
      lng: prospectPosition.lng + maxLngDelta,
    }
  );

  map.fitBounds(bounds, { top: 25, right: 10, bottom: 10, left: 10 });
};

const getGoogleMapsApiKey = (): string => {
  const env = getEnvironment();
  if (env === "production") {
    return GOOGLE_MAPS_API_KEYS.production;
  }
  return GOOGLE_MAPS_API_KEYS.staging;
};

type CapNearbyCustomersMapProps = {
  latitude: number;
  longitude: number;
  locations: NearbyCustomerLocation[];
  showZoomControls?: boolean;
  testId?: string;
};

type MapInnerProps = CapNearbyCustomersMapProps & {
  mapContainerRef: React.RefObject<HTMLDivElement>;
};

const MapInner = ({
  latitude,
  longitude,
  locations,
  showZoomControls = false,
  mapContainerRef,
}: MapInnerProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    const prospectPosition = { lat: latitude, lng: longitude };

    const map = new google.maps.Map(mapContainerRef.current, {
      center: prospectPosition,
      zoom: DEFAULT_MAP_ZOOM,
      mapTypeId: "satellite",
      disableDefaultUI: true,
      draggableCursor: "default",
      zoomControl: showZoomControls,
      zoomControlOptions: showZoomControls
        ? { position: google.maps.ControlPosition.RIGHT_CENTER }
        : undefined,
      minZoom: 12,
      maxZoom: 16,
      clickableIcons: false,
    });

    mapRef.current = map;
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    locations.forEach((location) => {
      markersRef.current.push(
        new google.maps.Marker({
          position: {
            lat: location.latitude,
            lng: location.longitude,
          },
          map,
          clickable: false,
          zIndex: 1,
          icon: {
            url: nearbyCustomerMapPin,
            scaledSize: new google.maps.Size(24, 24),
          },
        })
      );
    });

    markersRef.current.push(
      new google.maps.Marker({
        position: prospectPosition,
        map,
        clickable: false,
        zIndex: 2,
        icon: {
          url: prospectMapPin,
          scaledSize: new google.maps.Size(80, 80),
          anchor: new google.maps.Point(40, 40),
        },
      })
    );

    centerMapOnProspectWithNeighbors(map, prospectPosition, locations);

    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      mapRef.current = null;
    };
  }, [latitude, longitude, locations, mapContainerRef, showZoomControls]);

  return <MapSurface ref={mapContainerRef} />;
};

const CapNearbyCustomersMap = ({
  latitude,
  longitude,
  locations,
  showZoomControls = false,
  testId = "cap-nearby-customers-map",
}: CapNearbyCustomersMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);

  return (
    <MapWrapper data-testid={testId}>
      <Wrapper
        apiKey={getGoogleMapsApiKey()}
        callback={(status: Status) => {
          if (status === Status.FAILURE) {
            console.error("Failed to load Google Maps for nearby customers.");
          }
        }}
      >
        <MapInner
          latitude={latitude}
          longitude={longitude}
          locations={locations}
          showZoomControls={showZoomControls}
          mapContainerRef={mapContainerRef}
        />
      </Wrapper>
    </MapWrapper>
  );
};

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const MapSurface = styled.div`
  width: 100%;
  height: 100%;
`;

export { CapNearbyCustomersMap };
